import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ConfiguratorContext } from '../context';
import { getProduct, getAccessories } from '../../../api';
import { Alert, Radio, Button, Row, Col, Card, Divider, Spin } from 'antd';
import SpinLoader from '../../../components/UI/SpinLoader';
import { setQueryStringValuesWithoutNavigation } from '../../../utils/router';
import PageHeader from '../../../components/UI/PageHeader';
import { UndoOutlined } from "@ant-design/icons";
import ProductCard from '../../../components/Product/ProductCard';
import { useTranslation } from 'react-i18next';
import { availableCommands } from '../../../utils/productVersions';

const DOOR_TYPE = 'door';
const HANDLE_TYPE = 'handle';
const IMG_BASE = '/build/images/configurator/areo-accessories';

const COMMAND_ORDER = ['none', 'key', 'push_pull', 'push_to_open'];
const COMMAND_LABEL = {
    none: 'command_none',
    key: 'command_key',
    push_pull: 'command_push_pull',
    push_to_open: 'command_push_to_open',
};

const accessoryImage = (accessory): string | null => {
    if (accessory.image_url) return accessory.image_url;

    const attrs = Object.fromEntries((accessory.attributes || []).map((a) => [a.name, a.value]));

    if (attrs.locking_type === 'key') return `${IMG_BASE}/with_key.webp`;

    switch (attrs.lock_areo) {
        case 'lever': return `${IMG_BASE}/lever_white.webp`;
        case 'handle': return `${IMG_BASE}/handle_white.webp`;
        case 'button': return `${IMG_BASE}/round_button_white.webp`;
        default: return null;
    }
};

const shortLabels = (names: string[]): string[] => {
    if (names.length < 2) return names;

    let prefix = names[0];
    let suffix = names[0];
    for (const name of names) {
        while (prefix && !name.startsWith(prefix)) prefix = prefix.slice(0, -1);
        while (suffix && !name.endsWith(suffix)) suffix = suffix.slice(1);
    }

    prefix = prefix.includes(' ') ? prefix.slice(0, prefix.lastIndexOf(' ') + 1) : '';
    suffix = suffix.includes(' ') ? suffix.slice(suffix.indexOf(' ')) : '';

    return names.map((name) => {
        if (prefix.length + suffix.length >= name.length) return name;
        const core = name
            .slice(prefix.length, name.length - suffix.length)
            .replace(/^[\s\-–(]+|[\s\-–)]+$/g, '')
            .trim();
        return core || name;
    });
};

const AccessoryOptions = ({ items, value, onChange }) => {
    const labels = shortLabels(items.map((item) => item.name));

    return (
        <Radio.Group style={{ width: '100%' }} value={value} onChange={(e) => onChange(e.target.value)}>
            <Row gutter={[16, 16]}>
                {items.map((item, index) => {
                    const image = accessoryImage(item);

                    return (
                        <Col xs={24} sm={12} key={item.id}>
                            <Radio value={item.id}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                                    {image && (
                                        <img alt="" style={{ width: 128, height: 128, objectFit: 'contain' }} src={image} />
                                    )}
                                    <span>{labels[index]}</span>
                                </span>
                            </Radio>
                        </Col>
                    );
                })}
            </Row>
        </Radio.Group>
    );
};

const AccessoriesStep = () => {

    const { t } = useTranslation();

    const { state, selectVariant, resetState } = useContext(ConfiguratorContext);

    const userChoice = state.choice;

    const commands = useMemo(() => {
        if (!userChoice.product) return [];

        const groups = availableCommands(
            state.productVersions,
            userChoice.product.opening_areo,
            userChoice.product.frame_sides_areo,
            { width: userChoice.width, height: userChoice.height, measureType: userChoice.measureType }
        );

        return [...new Set(groups.map((g) => g.lock_predisposition))]
            .filter((c) => c in COMMAND_LABEL)
            .sort((a, b) => COMMAND_ORDER.indexOf(a) - COMMAND_ORDER.indexOf(b));
    }, [state.productVersions, userChoice]);

    const [command, setCommand] = useState<string | null>(null);

    const [loadingProduct, setLoadingProduct] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [product, setProduct] = useState<any>(null);
    const [doors, setDoors] = useState<any[]>([]);
    const [selectedDoorId, setSelectedDoorId] = useState<number | null>(null);
    const [handles, setHandles] = useState<any[]>([]);
    const [handlesLoading, setHandlesLoading] = useState(false);
    const [selectedHandleId, setSelectedHandleId] = useState<number | null>(null);

    useEffect(() => {
        if (commands.length === 1) setCommand(commands[0]);
    }, [commands]);

    useEffect(() => {
        if (!command || !userChoice.product) {
            setProduct(null); setDoors([]); setSelectedDoorId(null);
            return;
        }

        let cancelled = false;

        (async () => {
            setLoadingProduct(true);
            setError(null);

            const choiceWithCommand = { ...userChoice, product: { ...userChoice.product, lock_predisposition: command } };
            const productsRes = await getProduct(choiceWithCommand);
            if (cancelled) return;
            if (productsRes.error) { setError(productsRes.error); setLoadingProduct(false); return; }

            const resolvedProduct = productsRes.data?.data?.[0];
            if (!resolvedProduct) { setError(t('configurator.no_products_found')); setLoadingProduct(false); return; }

            const doorsRes = await getAccessories(resolvedProduct.id, DOOR_TYPE);
            if (cancelled) return;
            if (doorsRes.error) { setError(doorsRes.error); setLoadingProduct(false); return; }

            const doorList = doorsRes.data?.data || [];

            setProduct(resolvedProduct);
            setDoors(doorList);
            setSelectedDoorId(doorList.length === 1 ? doorList[0].id : null);
            setLoadingProduct(false);
        })();

        return () => { cancelled = true; };
        // userChoice.product is fixed once this step is reached; re-run only on command change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [command]);

    useEffect(() => {
        if (!product || !selectedDoorId) { setHandles([]); setSelectedHandleId(null); return; }

        let cancelled = false;

        (async () => {
            setHandlesLoading(true);
            setSelectedHandleId(null);

            const res = await getAccessories(product.id, HANDLE_TYPE, [selectedDoorId]);
            if (cancelled) return;
            if (res.error) { setError(res.error); setHandles([]); setHandlesLoading(false); return; }

            const handleList = res.data?.data || [];

            setHandles(handleList);
            // Only one finish available (e.g. Schlüssel): nothing to choose, assign it.
            setSelectedHandleId(handleList.length === 1 ? handleList[0].id : null);
            setHandlesLoading(false);
        })();

        return () => { cancelled = true; };
    }, [product, selectedDoorId]);

    const confirm = () => {
        const accessoryIds = [selectedDoorId, selectedHandleId].filter(Boolean);

        selectVariant({ ...product, accessory_ids: accessoryIds });

        setQueryStringValuesWithoutNavigation({
            variant_id: product.id,
            step: 3,
        });
    };

    if (!userChoice.product) {
        resetState();
        return null;
    }

    if (error) {
        return <Alert title={t('configurator.error')} description={error} type="error" showIcon />;
    }

    const needsCommandChoice = commands.length > 1;
    const needsDoorChoice = doors.length > 1;
    const needsHandleChoice = handles.length > 0;
    const canConfirm = Boolean(product) && (doors.length === 0 || Boolean(selectedDoorId)) && (!needsHandleChoice || Boolean(selectedHandleId));

    return (
        <div>
            <PageHeader
                title={t('configurator.select_handles')}
                subTitle={t('configurator.select_handles_subtitle')}
                extra={
                    <Button icon={<UndoOutlined />} type="default" onClick={() => resetState()}>
                        {t('configurator.cancel')}
                    </Button>
                }
            />

            <Row gutter={[24, 24]}>

                <Col xs={24} lg={12}>
                    <ProductCard product={userChoice.product} userChoice={userChoice} />
                </Col>

                <Col xs={24} lg={12}>

                    {commands.length === 0 && (
                        <Alert type="warning" showIcon title={t('configurator.no_products_found')} />
                    )}

                    {needsCommandChoice && (
                        <Card title={t('configurator.select_command')} style={{ marginBottom: 24 }}>
                            <Radio.Group value={command} onChange={(e) => setCommand(e.target.value)}>
                                <Row gutter={[16, 16]}>
                                    {commands.map((c) => (
                                        <Col xs={24} sm={12} key={c}>
                                            <Radio value={c}>{t(`configurator.${COMMAND_LABEL[c]}`)}</Radio>
                                        </Col>
                                    ))}
                                </Row>
                            </Radio.Group>
                        </Card>
                    )}

                    {command && (loadingProduct ? <SpinLoader /> : (
                        <>
                            {needsDoorChoice && (
                                <Card title={t('configurator.select_processing')} style={{ marginBottom: 24 }}>
                                    <AccessoryOptions items={doors} value={selectedDoorId} onChange={setSelectedDoorId} />
                                </Card>
                            )}

                            <Card title={t('configurator.handles')}>
                                {!selectedDoorId ? (
                                    <Alert type="info" showIcon title={t('configurator.select_processing')} />
                                ) : handlesLoading ? (
                                    <Spin />
                                ) : needsHandleChoice ? (
                                    <AccessoryOptions items={handles} value={selectedHandleId} onChange={setSelectedHandleId} />
                                ) : (
                                    <Alert type="info" showIcon title={t('configurator.no_handles_needed')} />
                                )}

                                <Divider />

                                <Button type="primary" size="large" disabled={!canConfirm} onClick={confirm}>
                                    {t('configurator.select')}
                                </Button>
                            </Card>
                        </>
                    ))}

                </Col>
            </Row>
        </div>
    );
}

export default AccessoriesStep;
